import React, { useState, useEffect, useCallback, useRef } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { searchUsers } from './businessApi';
import {
  initiateTransfer, generateTransferContract, getTransfers,
  onboardTransferSeller, getTransferOnboardStatus,
  scheduleTransferInstallments, getTransferInstallments,
  createTransferCheckout, getTransferPaymentStatus,
} from './businessApi';

const F = {
  bg: '#14141a', panel: '#1e1e28',
  border: 'rgba(255,255,255,0.1)', input: '#12121a',
  inputBorder: 'rgba(255,255,255,0.15)',
};
const inp = (x = {}) => ({
  background: F.input, border: `1px solid ${F.inputBorder}`,
  borderRadius: 10, padding: '10px 14px', color: C.white,
  fontSize: 13, width: '100%', boxSizing: 'border-box',
  outline: 'none', fontFamily: 'inherit', ...x,
});
const card = (x = {}) => ({
  background: F.panel, border: `1px solid ${F.border}`,
  borderRadius: 14, padding: 18, marginBottom: 14, ...x,
});
const lbl = { fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 6, display: 'block' };

const COMMISSION_RATE = 0.08; // 8%

function fmtEuro(n, cur = 'EUR') {
  const sym = cur === 'USD' ? '$' : cur === 'GBP' ? '£' : '€';
  return `${sym} ${parseFloat(n || 0).toFixed(2)}`;
}

function mdToHtml(md) {
  return (md || '')
    .replace(/^### (.+)$/gm, '<h3 style="color:#5B9CF6;margin:16px 0 6px;font-size:13px;letter-spacing:1px">$1</h3>')
    .replace(/^## (.+)$/gm,  '<h2 style="color:#7EC8E3;margin:18px 0 8px;font-size:14px">$1</h2>')
    .replace(/^# (.+)$/gm,   '<h1 style="color:#fff;margin:0 0 12px;font-size:17px">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#fff">$1</strong>')
    .replace(/^---$/gm, '<hr style="border:none;border-top:1px solid rgba(255,255,255,0.1);margin:14px 0">')
    .replace(/^- (.+)$/gm,  '<li style="color:rgba(255,255,255,0.8);margin:3px 0;padding-left:8px">$1</li>')
    .replace(/\n/g, '<br>');
}

// ─────────────────────────────────────────────────────────────────────────────
// Step indicator
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  { n: 1, nl: 'Partijen',  en: 'Parties'  },
  { n: 2, nl: 'Contract',  en: 'Contract' },
  { n: 3, nl: 'Betaling',  en: 'Payment'  },
];

function StepBar({ step, nl }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
      {STEPS.map(s => (
        <div key={s.n} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ width: '100%', height: 3, borderRadius: 2, background: step >= s.n ? _T.accent : F.border }} />
          <div style={{ fontSize: 10, color: step >= s.n ? _T.accent : C.silver2, fontWeight: step === s.n ? 700 : 400 }}>
            {nl ? s.nl : s.en}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 1 — Parties, price, installments
// ─────────────────────────────────────────────────────────────────────────────
function Step1({ nl, currentUserId, userName, onNext }) {
  const [search, setSearch]           = useState('');
  const [results, setResults]         = useState([]);
  const [newOwners, setNewOwners]     = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [price, setPrice]             = useState('');
  const [currency, setCurrency]       = useState('EUR');
  const [instCount, setInstCount]     = useState(1);
  const [instInterval, setInstInterval] = useState('immediate');
  const [notes, setNotes]             = useState('');
  const [loading, setLoading]         = useState(false);

  const doSearch = useCallback(async (q) => {
    setSearch(q);
    if (q.trim().length < 2) { setResults([]); return; }
    const r = await searchUsers(q, currentUserId);
    setResults(Array.isArray(r) ? r.slice(0, 6) : []);
  }, [currentUserId]);

  const addOwner  = u => { if (!newOwners.find(x => x.id === u.id)) setNewOwners(o => [...o, u]); setSearch(''); setResults([]); };
  const addEmail  = () => { const e = inviteEmail.trim(); if (!e || !e.includes('@')) return; if (!newOwners.find(x => x.email === e)) setNewOwners(o => [...o, { id: `inv_${e}`, name: e, email: e, isInvite: true }]); setInviteEmail(''); };
  const rmOwner   = id => setNewOwners(o => o.filter(u => u.id !== id));

  const commission = parseFloat(price || 0) * COMMISSION_RATE;
  const instAmt    = instCount > 0 ? parseFloat(price || 0) / instCount : 0;
  const firstReceive = instAmt - commission; // seller gets first_installment - full_commission

  const handleNext = async () => {
    if (!newOwners.length) return alert(nl ? 'Voeg minstens één nieuwe eigenaar toe.' : 'Add at least one new owner.');
    if (!price || parseFloat(price) <= 0) return alert(nl ? 'Voer een koopsom in.' : 'Enter a purchase price.');
    if (firstReceive < 0) return alert(nl ? `De eerste termijn (${fmtEuro(instAmt, currency)}) moet groter zijn dan de commissie (${fmtEuro(commission, currency)}).` : `First installment (${fmtEuro(instAmt, currency)}) must exceed commission (${fmtEuro(commission, currency)}).`);
    setLoading(true);
    onNext({ newOwners, price: parseFloat(price), currency, instCount, instInterval, notes, currentUserId });
    setLoading(false);
  };

  return (
    <>
      {/* Seller */}
      <div style={card()}>
        <span style={lbl}>{nl ? 'HUIDIGE EIGENAAR (VERKOPER)' : 'CURRENT OWNER (SELLER)'}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: _T.accent, fontWeight: 700 }}>
            {(userName || 'U')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ color: C.white, fontWeight: 600 }}>{userName}</div>
            <div style={{ color: C.silver, fontSize: 11 }}>ID: {currentUserId}</div>
          </div>
        </div>
      </div>

      {/* New owners */}
      <div style={card()}>
        <span style={lbl}>{nl ? 'NIEUWE EIGENAAR(S) (KOPER)' : 'NEW OWNER(S) (BUYER)'}</span>
        <input style={{ ...inp(), marginBottom: 6 }} placeholder={nl ? 'Zoek gebruiker...' : 'Search user...'}
          value={search} onChange={e => doSearch(e.target.value)} />
        {results.map(u => (
          <div key={u.id} onClick={() => addOwner(u)}
            style={{ background: _T.bg3, border: `1px solid ${F.border}`, borderRadius: 10, padding: '9px 12px', marginBottom: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: _T.accent, fontWeight: 700 }}>
              {(u.name || '?')[0].toUpperCase()}
            </div>
            <div><div style={{ color: C.white, fontSize: 13 }}>{u.name || u.username}</div><div style={{ color: C.silver, fontSize: 11 }}>{u.email}</div></div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 8, marginBottom: 12 }}>
          <input style={inp({ flex: 1 })} type="email" placeholder={nl ? 'Uitnodigen via email...' : 'Invite by email...'}
            value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && addEmail()} />
          <button onClick={addEmail} style={{ background: F.panel, border: `1px solid ${F.border}`, borderRadius: 10, color: C.silver, padding: '10px 14px', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>+</button>
        </div>
        {newOwners.map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: `${_T.accent}10`, border: `1px solid ${_T.accent}30`, borderRadius: 10, padding: '9px 12px', marginBottom: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: _T.accent, fontWeight: 700 }}>
                {(u.name || u.email || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ color: C.white, fontSize: 13 }}>{u.name || u.email}</div>
                {u.isInvite && <div style={{ color: C.silver, fontSize: 10 }}>{nl ? 'Uitnodiging per email' : 'Email invite'}</div>}
              </div>
            </div>
            <button onClick={() => rmOwner(u.id)} style={{ background: 'none', border: 'none', color: '#C05050', fontSize: 16, cursor: 'pointer' }}>✕</button>
          </div>
        ))}
      </div>

      {/* Price */}
      <div style={card()}>
        <span style={lbl}>{nl ? 'KOOPSOM & BETAALPLAN' : 'PURCHASE PRICE & PAYMENT PLAN'}</span>
        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 2 }}>
            <span style={lbl}>{nl ? 'TOTALE KOOPSOM *' : 'TOTAL PRICE *'}</span>
            <input type="number" style={inp()} placeholder="0.00" value={price} onChange={e => setPrice(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <span style={lbl}>{nl ? 'VALUTA' : 'CURRENCY'}</span>
            <select style={inp()} value={currency} onChange={e => setCurrency(e.target.value)}>
              {['EUR','USD','GBP'].map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
          <div style={{ flex: 1 }}>
            <span style={lbl}>{nl ? 'AANTAL TERMIJNEN' : 'INSTALLMENTS'}</span>
            <select style={inp()} value={instCount} onChange={e => setInstCount(parseInt(e.target.value))}>
              {[1,2,3,4,6,12].map(n => <option key={n} value={n}>{n === 1 ? (nl ? '1 (direct)' : '1 (lump sum)') : n}</option>)}
            </select>
          </div>
          {instCount > 1 && (
            <div style={{ flex: 1 }}>
              <span style={lbl}>{nl ? 'INTERVAL' : 'INTERVAL'}</span>
              <select style={inp()} value={instInterval} onChange={e => setInstInterval(e.target.value)}>
                <option value="monthly">{nl ? 'Maandelijks' : 'Monthly'}</option>
                <option value="quarterly">{nl ? 'Per kwartaal' : 'Quarterly'}</option>
              </select>
            </div>
          )}
        </div>

        {/* Commission breakdown */}
        {parseFloat(price || 0) > 0 && (
          <div style={{ background: 'rgba(91,156,246,0.08)', border: '1px solid rgba(91,156,246,0.2)', borderRadius: 10, padding: 14 }}>
            <div style={{ color: C.silver, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>{nl ? 'BETALINGSOVERZICHT' : 'PAYMENT BREAKDOWN'}</div>
            {[
              { label: nl ? 'Koopsom' : 'Total price',                     val: fmtEuro(price, currency),       color: C.white },
              { label: nl ? `SenseLifting commissie (8%)` : `SenseLifting commission (8%)`, val: fmtEuro(commission, currency), color: '#C8A84B' },
              { label: nl ? `Per termijn (${instCount}x)` : `Per installment (${instCount}x)`, val: fmtEuro(instAmt, currency), color: _T.accent },
              { label: nl ? 'Verkoper ontvangt (termijn 1)' : 'Seller receives (payment 1)', val: fmtEuro(Math.max(0, firstReceive), currency), color: '#4AC87A' },
            ].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                <span style={{ color: C.silver }}>{r.label}</span>
                <span style={{ color: r.color, fontWeight: 600 }}>{r.val}</span>
              </div>
            ))}
            {firstReceive < 0 && (
              <div style={{ color: '#C05050', fontSize: 12, marginTop: 6 }}>
                ⚠ {nl ? 'Eerste termijn te klein om commissie te dekken. Verhoog het bedrag of verminder het aantal termijnen.' : 'First installment too small to cover commission. Increase price or reduce installments.'}
              </div>
            )}
            {instCount > 1 && (
              <div style={{ color: C.silver2, fontSize: 11, marginTop: 6, lineHeight: 1.4 }}>
                {nl ? `Termijnen 2–${instCount}: ${fmtEuro(instAmt, currency)} direct naar verkoper (geen commissie).` : `Installments 2–${instCount}: ${fmtEuro(instAmt, currency)} direct to seller (no commission).`}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={card()}>
        <span style={lbl}>{nl ? 'BIJZONDERE VOORWAARDEN (optioneel)' : 'SPECIAL CONDITIONS (optional)'}</span>
        <textarea style={{ ...inp(), resize: 'none' }} rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
      </div>

      <button onClick={handleNext} disabled={loading || !newOwners.length || !price || firstReceive < 0}
        style={{ width: '100%', background: (newOwners.length && price && firstReceive >= 0) ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : F.panel, border: 'none', borderRadius: 12, color: (newOwners.length && price && firstReceive >= 0) ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 14, padding: '14px', cursor: 'pointer', marginBottom: 8 }}>
        {loading ? '...' : (nl ? 'Contract genereren →' : 'Generate contract →')}
      </button>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — Contract
// ─────────────────────────────────────────────────────────────────────────────
function Step2({ nl, contractMd, loading, onRegenerate, onPrint, onNext, onBack }) {
  return (
    <>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ color: _T.accent, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
            ⚖️ {nl ? 'Contract wordt gegenereerd...' : 'Generating contract...'}
          </div>
        </div>
      ) : (
        <>
          <div style={{ ...card(), maxHeight: '44vh', overflowY: 'auto' }}>
            <div dangerouslySetInnerHTML={{ __html: mdToHtml(contractMd) }}
              style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, lineHeight: 1.7 }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button onClick={onRegenerate} style={{ flex: 1, background: F.panel, border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
              ↻ {nl ? 'Opnieuw' : 'Regenerate'}
            </button>
            <button onClick={onPrint} style={{ flex: 1, background: F.panel, border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
              🖨 {nl ? 'Afdrukken' : 'Print'}
            </button>
          </div>
          <button onClick={onNext} style={{ width: '100%', background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '14px', cursor: 'pointer', marginBottom: 8 }}>
            {nl ? 'Akkoord & doorgaan naar betaling →' : 'Agree & continue to payment →'}
          </button>
          <button onClick={onBack} style={{ width: '100%', background: 'none', border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
            ← {nl ? 'Terug' : 'Back'}
          </button>
        </>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STEP 3 — Stripe Payment
// ─────────────────────────────────────────────────────────────────────────────
function Step3({ nl, businessId, tid, transfer, installments, onDone, onBack }) {
  const [onboardUrl, setOnboardUrl]     = useState(null);
  const [onboardStatus, setOnboardStatus] = useState({ ready: false, checked: false });
  const [checkingOnboard, setCheckingOnboard] = useState(false);
  const [pendingSession, setPendingSession] = useState(null); // { session_id, inst }
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [instList, setInstList]         = useState(installments || []);
  const [creatingCheckout, setCreatingCheckout] = useState(null);
  const pollRef = useRef(null);

  const currency = transfer?.currency || 'EUR';
  const totalPrice = parseFloat(transfer?.price || 0);
  const commission = totalPrice * COMMISSION_RATE;

  const checkOnboardStatus = useCallback(async () => {
    if (!tid) return;
    setCheckingOnboard(true);
    const r = await getTransferOnboardStatus(businessId, tid);
    setOnboardStatus({ ready: r.ready, checked: true, ...r });
    setCheckingOnboard(false);
    return r.ready;
  }, [businessId, tid]);

  // Auto-check onboard status on mount
  useEffect(() => { checkOnboardStatus(); }, [checkOnboardStatus]);

  // Reload installments
  const reloadInstallments = useCallback(async () => {
    const r = await getTransferInstallments(businessId, tid);
    if (Array.isArray(r)) setInstList(r);
  }, [businessId, tid]);

  useEffect(() => { reloadInstallments(); }, [reloadInstallments]);

  const handleOnboard = async () => {
    const r = await onboardTransferSeller(businessId, tid);
    if (r.url) {
      setOnboardUrl(r.url);
      window.open(r.url, '_blank', 'noopener');
    }
  };

  const handlePayInstallment = async (instNum) => {
    setCreatingCheckout(instNum);
    const r = await createTransferCheckout(businessId, tid, { installment_number: instNum });
    if (r.error) { alert(r.error); setCreatingCheckout(null); return; }
    // Store session for polling
    setPendingSession({ session_id: r.session_id, inst: instNum });
    // Open Stripe checkout in same window
    window.location.href = r.url;
    setCreatingCheckout(null);
  };

  const handleCheckPayment = async () => {
    if (!pendingSession) return;
    setCheckingPayment(true);
    const r = await getTransferPaymentStatus(businessId, tid, pendingSession.session_id, pendingSession.inst);
    if (Array.isArray(r.installments)) setInstList(r.installments);
    if (r.status === 'paid' && pendingSession.inst === 1) {
      setPendingSession(null);
      onDone();
    }
    setCheckingPayment(false);
  };

  // Check URL params for return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('transfer_paid') === '1') {
      reloadInstallments();
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      onDone();
    }
  }, [reloadInstallments, onDone]);

  const firstInst = instList.find(i => i.installment_number === 1);
  const firstPaid  = firstInst?.status === 'paid';
  const allPaid    = instList.length > 0 && instList.every(i => i.status === 'paid');

  return (
    <>
      {/* Commission info box */}
      <div style={{ background: 'rgba(91,156,246,0.08)', border: '1px solid rgba(91,156,246,0.2)', borderRadius: 12, padding: 14, marginBottom: 14 }}>
        <div style={{ color: '#5B9CF6', fontSize: 12, fontWeight: 700, letterSpacing: 1, marginBottom: 10 }}>
          {nl ? 'BETALINGSSTRUCTUUR' : 'PAYMENT STRUCTURE'}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span style={{ color: C.silver }}>{nl ? 'Koopsom' : 'Total'}</span>
          <span style={{ color: C.white, fontWeight: 700 }}>{fmtEuro(totalPrice, currency)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
          <span style={{ color: C.silver }}>{nl ? 'SenseLifting commissie (8%)' : 'SenseLifting commission (8%)'}</span>
          <span style={{ color: '#C8A84B', fontWeight: 700 }}>− {fmtEuro(commission, currency)}</span>
        </div>
        <div style={{ borderTop: `1px solid ${F.border}`, marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
          <span style={{ color: C.silver }}>{nl ? 'Commissie afgetrokken van termijn 1' : 'Commission deducted from payment 1'}</span>
          <span style={{ color: '#4AC87A', fontWeight: 700 }}>Stripe Connect</span>
        </div>
      </div>

      {/* Step A: Seller Stripe Connect onboarding */}
      <div style={card()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div>
            <div style={{ color: C.white, fontWeight: 700, fontSize: 14 }}>
              {nl ? '1. Verkoper uitbetalingsaccount' : '1. Seller payout account'}
            </div>
            <div style={{ color: C.silver, fontSize: 12, marginTop: 3 }}>
              {nl ? 'Verkoper koppelt bankrekening via Stripe' : 'Seller connects bank account via Stripe'}
            </div>
          </div>
          {onboardStatus.checked && (
            <div style={{ fontSize: 20 }}>{onboardStatus.ready ? '✅' : '⏳'}</div>
          )}
        </div>

        {!onboardStatus.ready ? (
          <>
            <div style={{ color: C.silver, fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
              {nl ? 'De verkoper moet een Stripe Connect account aanmaken om uitbetalingen te ontvangen. Stripe beheert de bankgegevens veilig — SenseLifting ziet deze niet.' : 'The seller must create a Stripe Connect account to receive payouts. Stripe securely manages bank details — SenseLifting never sees them.'}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleOnboard}
                style={{ flex: 2, background: `linear-gradient(135deg,#635BFF,#7B73FF)`, border: 'none', borderRadius: 10, color: '#fff', fontWeight: 700, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
                🔗 {nl ? 'Stripe account instellen' : 'Setup Stripe account'}
              </button>
              <button onClick={checkOnboardStatus} disabled={checkingOnboard}
                style={{ flex: 1, background: F.input, border: `1px solid ${F.border}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '11px', cursor: 'pointer' }}>
                {checkingOnboard ? '...' : (nl ? 'Status check' : 'Check status')}
              </button>
            </div>
            {onboardUrl && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.silver, lineHeight: 1.5 }}>
                {nl ? 'Link geopend in nieuw venster. Klik na voltooiing op "Status check".' : 'Link opened in new window. Click "Check status" after completing.'}
              </div>
            )}
          </>
        ) : (
          <div style={{ background: 'rgba(74,200,122,0.1)', border: '1px solid rgba(74,200,122,0.3)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#4AC87A', fontWeight: 600 }}>
            ✓ {nl ? 'Stripe account actief — uitbetalingen ingeschakeld' : 'Stripe account active — payouts enabled'}
          </div>
        )}
      </div>

      {/* Step B: Installment payments */}
      <div style={card()}>
        <div style={{ color: C.white, fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
          {nl ? '2. Termijnbetalingen' : '2. Installment payments'}
        </div>

        {instList.length === 0 ? (
          <div style={{ color: C.silver, fontSize: 13, textAlign: 'center', padding: 16 }}>
            {nl ? 'Termijnen worden geladen...' : 'Loading installments...'}
          </div>
        ) : instList.map(inst => {
          const isPaid    = inst.status === 'paid';
          const isFirst   = inst.installment_number === 1;
          const fee       = parseFloat(inst.application_fee || 0);
          const sellerGets = parseFloat(inst.amount) - fee;
          const canPay    = onboardStatus.ready && !isPaid && (isFirst || (instList[inst.installment_number - 2]?.status === 'paid'));

          return (
            <div key={inst.id} style={{ background: isPaid ? 'rgba(74,200,122,0.06)' : F.input, border: `1px solid ${isPaid ? 'rgba(74,200,122,0.3)' : F.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                  <span style={{ color: isPaid ? '#4AC87A' : C.white, fontWeight: 700, fontSize: 13 }}>
                    {nl ? `Termijn ${inst.installment_number}` : `Payment ${inst.installment_number}`}
                  </span>
                  <span style={{ background: isPaid ? 'rgba(74,200,122,0.15)' : 'rgba(200,168,75,0.15)', color: isPaid ? '#4AC87A' : '#C8A84B', border: `1px solid ${isPaid ? 'rgba(74,200,122,0.3)' : 'rgba(200,168,75,0.3)'}`, borderRadius: 6, padding: '1px 7px', fontSize: 10, fontWeight: 700 }}>
                    {isPaid ? '✓ BETAALD' : 'OPENSTAAND'}
                  </span>
                </div>
                <div style={{ color: C.silver, fontSize: 11 }}>
                  {nl ? 'Vervalt' : 'Due'}: {inst.due_date}
                  {fee > 0 && ` · ${nl ? 'Commissie' : 'Commission'}: ${fmtEuro(fee, currency)} · ${nl ? 'Verkoper ontvangt' : 'Seller gets'}: ${fmtEuro(sellerGets, currency)}`}
                  {isPaid && inst.paid_at && ` · ${nl ? 'Betaald op' : 'Paid'}: ${inst.paid_at.slice(0, 10)}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 12 }}>
                <span style={{ color: _T.accent, fontWeight: 700, fontSize: 15 }}>{fmtEuro(inst.amount, currency)}</span>
                {!isPaid && canPay && (
                  <button onClick={() => handlePayInstallment(inst.installment_number)} disabled={creatingCheckout === inst.installment_number}
                    style={{ background: `linear-gradient(135deg,#635BFF,#7B73FF)`, border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: 12, padding: '8px 14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {creatingCheckout === inst.installment_number ? '...' : '💳 Pay'}
                  </button>
                )}
                {!isPaid && !canPay && !onboardStatus.ready && (
                  <span style={{ color: C.silver2, fontSize: 11 }}>{nl ? 'Wacht op verkoper' : 'Awaiting seller'}</span>
                )}
              </div>
            </div>
          );
        })}

        {pendingSession && !firstPaid && (
          <div style={{ background: 'rgba(91,156,246,0.1)', border: '1px solid rgba(91,156,246,0.3)', borderRadius: 10, padding: '12px 14px', marginTop: 8 }}>
            <div style={{ color: '#5B9CF6', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {nl ? 'Betaling verwerkt bij Stripe...' : 'Payment processing at Stripe...'}
            </div>
            <div style={{ color: C.silver, fontSize: 12, marginBottom: 10 }}>
              {nl ? 'Heb je de betaling afgerond? Klik hieronder om de status te controleren.' : 'Completed the payment? Click below to check status.'}
            </div>
            <button onClick={handleCheckPayment} disabled={checkingPayment}
              style={{ width: '100%', background: F.panel, border: `1px solid ${F.border}`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '11px', cursor: 'pointer' }}>
              {checkingPayment ? '...' : (nl ? '↻ Betaalstatus controleren' : '↻ Check payment status')}
            </button>
          </div>
        )}
      </div>

      {allPaid && (
        <button onClick={onDone}
          style={{ width: '100%', background: '#4AC87A', border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '14px', cursor: 'pointer', marginBottom: 8 }}>
          ✓ {nl ? 'Overdracht voltooid' : 'Transfer complete'}
        </button>
      )}

      <button onClick={onBack}
        style={{ width: '100%', background: 'none', border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
        ← {nl ? 'Terug naar contract' : 'Back to contract'}
      </button>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function BusinessOwnershipTransferScreen({ lang, businessId, business, currentUserId, userName }) {
  const nl = lang === 'nl';
  const [step, setStep]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [transferId, setTransferId] = useState(null);
  const [transferData, setTransferData] = useState(null);
  const [contractMd, setContractMd] = useState('');
  const [installments, setInstallments] = useState([]);
  const [history, setHistory]       = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [done, setDone]             = useState(false);

  const handleStep1 = async ({ newOwners, price, currency, instCount, instInterval, notes, currentUserId: fromId }) => {
    setLoading(true);
    try {
      const res = await initiateTransfer(businessId, {
        new_owner_ids:  newOwners.filter(u => !u.isInvite).map(u => u.id),
        invite_emails:  newOwners.filter(u => u.isInvite).map(u => u.email),
        price, currency, notes, from_owner_id: fromId,
      });
      if (!res.transfer_id) throw new Error('Failed to create transfer');
      setTransferId(res.transfer_id);
      setTransferData({ price, currency, installment_count: instCount, installment_interval: instInterval, from_owner_id: fromId });

      // Schedule installments
      await scheduleTransferInstallments(businessId, res.transfer_id, { installment_count: instCount, interval: instInterval });

      // Generate contract
      setStep(2); setLoading(true);
      const cRes = await generateTransferContract(businessId, { transfer_id: res.transfer_id });
      setContractMd(cRes.contract_markdown || '');
    } catch (e) {
      alert(nl ? 'Fout: ' + e.message : 'Error: ' + e.message);
    } finally { setLoading(false); }
  };

  const handleRegenerate = async () => {
    setLoading(true);
    const r = await generateTransferContract(businessId, { transfer_id: transferId });
    setContractMd(r.contract_markdown || '');
    setLoading(false);
  };

  const handlePrintContract = () => {
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Contract</title><style>body{font-family:Arial,sans-serif;margin:40px;color:#111;line-height:1.6}h1,h2,h3{color:#1a3a6b}hr{border:none;border-top:1px solid #ddd;margin:20px 0}@media print{body{margin:20px}}</style></head><body>${mdToHtml(contractMd).replace(/style="[^"]*color:[^"]*"/g, '')}</body></html>`;
    const w = window.open('', '_blank'); w.document.write(html); w.document.close(); setTimeout(() => w.print(), 300);
  };

  const handleStep2Next = async () => {
    // Load installments for step 3
    const r = await getTransferInstallments(businessId, transferId);
    setInstallments(Array.isArray(r) ? r : []);
    setStep(3);
  };

  const handleDone = () => setDone(true);

  const loadHistory = async () => {
    const d = await getTransfers(businessId);
    setHistory(Array.isArray(d) ? d : []);
    setShowHistory(true);
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 20px' }}>
        <div style={{ fontSize: 60, marginBottom: 16 }}>✅</div>
        <div style={{ color: C.white, fontSize: 20, fontWeight: 700, marginBottom: 8 }}>
          {nl ? 'Eigendomsoverdracht voltooid!' : 'Ownership transfer complete!'}
        </div>
        <div style={{ color: C.silver, fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
          {nl ? 'De nieuwe eigenaar is ingesteld. De commissie van 8% is verwerkt via Stripe Connect. Resterende termijnen zijn beschikbaar in dit scherm.' : 'The new owner has been set. The 8% commission has been processed via Stripe Connect. Remaining installments are available in this screen.'}
        </div>
        <button onClick={() => { setDone(false); setStep(1); setTransferId(null); setContractMd(''); setInstallments([]); }}
          style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontWeight: 700, fontSize: 14, padding: '13px 28px', cursor: 'pointer' }}>
          {nl ? 'Terug naar overzicht' : 'Back to overview'}
        </button>
      </div>
    );
  }

  return (
    <div>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ color: C.white, fontSize: 18, fontWeight: 700, marginBottom: 6 }}>
          🔐 {nl ? 'Eigendomsoverdracht' : 'Ownership Transfer'}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
          <div style={{ background: 'rgba(99,91,255,0.15)', border: '1px solid rgba(99,91,255,0.4)', borderRadius: 8, padding: '3px 10px', fontSize: 11, color: '#7B73FF', fontWeight: 700 }}>Stripe Connect</div>
          <div style={{ color: C.silver, fontSize: 12 }}>{nl ? '8% commissie · Veilige uitbetaling · Termijnen mogelijk' : '8% commission · Secure payout · Installments supported'}</div>
        </div>
      </div>

      {/* Warning */}
      <div style={{ background: 'rgba(192,80,80,0.1)', border: '1px solid rgba(192,80,80,0.3)', borderRadius: 12, padding: '11px 14px', marginBottom: 18 }}>
        <div style={{ color: '#C05050', fontWeight: 700, fontSize: 12 }}>⚠ {nl ? 'Onomkeerbare actie — eigendom gaat definitief over na eerste betaling.' : 'Irreversible — ownership transfers permanently after first payment.'}</div>
      </div>

      <StepBar step={step} nl={nl} />

      {step === 1 && (
        <Step1 nl={nl} currentUserId={currentUserId} userName={userName} onNext={handleStep1} />
      )}

      {step === 2 && (
        <Step2
          nl={nl}
          contractMd={contractMd}
          loading={loading}
          onRegenerate={handleRegenerate}
          onPrint={handlePrintContract}
          onNext={handleStep2Next}
          onBack={() => setStep(1)}
        />
      )}

      {step === 3 && (
        <Step3
          nl={nl}
          businessId={businessId}
          tid={transferId}
          transfer={transferData}
          installments={installments}
          onDone={handleDone}
          onBack={() => setStep(2)}
        />
      )}

      {/* History */}
      {step === 1 && (
        <div style={{ marginTop: 24 }}>
          <button onClick={loadHistory}
            style={{ width: '100%', background: 'none', border: `1px solid ${F.border}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '11px', cursor: 'pointer' }}>
            📋 {nl ? 'Overdrachtshistorie' : 'Transfer history'}
          </button>
          {showHistory && history.map(t => (
            <div key={t.id} style={{ ...card({ padding: 14, marginTop: 8 }) }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ color: C.silver }}>{t.created_at?.slice(0, 10)}</span>
                <span style={{ color: t.status === 'confirmed' ? '#4AC87A' : '#C8A84B', fontWeight: 700 }}>{t.status}</span>
              </div>
              {parseFloat(t.price || 0) > 0 && (
                <div style={{ color: _T.accent, fontSize: 14, fontWeight: 700, marginTop: 4 }}>{t.currency} {parseFloat(t.price).toFixed(2)}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
